/**
 * Hanlyn Quick Shop Drawer — variant selection fix
 *
 * WHY THIS EXISTS
 * hanlyn-quick-shop-drawer.js clones <variant-selects> into .hanlyn-qs__variants
 * and <product-form> into .hanlyn-qs__form-wrapper. Because those two clones do
 * NOT live inside a <product-info> element, Dawn's product-info.js never runs
 * updateVariantInputs() for them, so the cloned form's hidden input[name="id"]
 * stays frozen at product.selected_or_first_available_variant — the first size.
 * Selecting a size in the drawer only toggled aria-pressed, so every quick-shop
 * add-to-cart posted the smallest size.
 *
 * WHAT THIS DOES
 * Resolves the selected variant from the product's own JSON and writes it into
 * the cloned form's hidden id input on every change. Also namespaces the cloned
 * ids so the drawer cannot cross-wire with the PDP's own form when it is opened
 * on a product page.
 *
 * LOAD ORDER: after hanlyn-quick-shop-drawer.js.
 *
 * ===========================================================================
 * TWO BUGS FIXED 2026-08-21. Both are easy to reintroduce. Please read.
 * ===========================================================================
 *
 * BUG 1 — sync() wrote into the subtree its own observer was watching.
 *
 * Symptom: opening the quick shop drawer pinned the main thread and the whole
 * tab stopped responding. Shoppers reported the size options as unclickable and
 * the site as "frozen" — no click could be processed at all, so this read as a
 * variant-picker bug when it was really a hung renderer.
 *
 * The loop: start() observes the drawer host with { childList: true,
 * subtree: true }. The observer callback runs onDrawerPopulated() -> sync(),
 * and sync() set priceTarget.textContent and label.textContent. Assigning
 * textContent replaces the child text node, which is a childList mutation
 * INSIDE the observed subtree — even when the new string is identical to the
 * old one. That queued a fresh record, which re-ran the callback, which wrote
 * again, forever. MutationObserver callbacks drain as microtasks, so the queue
 * never yielded to the event loop.
 *
 * Two independent guards now prevent this, and both should stay:
 *   1. Every DOM write in sync() is conditional — nothing is assigned unless
 *      the value actually differs. Once settled, sync() mutates nothing and the
 *      cycle terminates on its own.
 *   2. applyDom() disconnects the observer around any code that mutates the
 *      drawer, then discards the records generated in the meantime with
 *      takeRecords() before reconnecting.
 *
 * A re-entrancy boolean is NOT sufficient here and was deliberately not used:
 * observer callbacks fire asynchronously, so the flag is already back to false
 * by the time the self-inflicted records are delivered.
 *
 * BUG 2 — namespaceClone()'s guard could never be true. See its own note.
 *
 * ===========================================================================
 * 2026-10-21 (v2.26) — PRE-LAUNCH PRODUCTS. Please read.
 * ===========================================================================
 *
 * sync() used to re-enable the submit button and write "Add to cart" whenever
 * the chosen variant had stock. On a product still behind the launch gate
 * (snippets/hanlyn-launch-gate.liquid) that turned the server-disabled
 * "Available Oct 22nd" button into a working add to cart, so pre-launch
 * products could be bought from the quick shop. It also fought
 * assets/hanlyn-launch-gate.js, which re-locks the button: each script's write
 * triggered the other's observer, forever.
 *
 * A form is pre-launch when the cloned form carries .hanlyn-launch-note (the
 * gate renders it inside product-form, so it travels with the clone). In that
 * case sync() still writes the variant id and price, but leaves the button
 * disabled and does not touch its label. After the launch instant the product
 * page renders without the note, so the drawer behaves normally again.
 */
(function () {
  'use strict';

  var QS_FORM_ID = 'product-form-hanlyn-qs';
  var OBSERVER_CONFIG = {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['href', 'open'],
  };

  var productCache = {};
  var currentPath = null;
  var currentProduct = null;
  var observer = null;
  var observedHost = null;

  /**
   * Run a function that mutates the drawer without the observer hearing its own
   * echo. takeRecords() drains anything queued while we were detached so those
   * records are not delivered on reconnect.
   */
  function applyDom(fn) {
    if (observer) observer.disconnect();
    try {
      fn();
    } finally {
      if (observer && observedHost) {
        observer.takeRecords();
        observer.observe(observedHost, OBSERVER_CONFIG);
      }
    }
  }

  /** Assign only when the value differs, so a settled DOM stays untouched. */
  function setText(el, value) {
    if (el && el.textContent !== value) el.textContent = value;
  }

  function drawerRoot() {
    return document.querySelector('hanlyn-quick-shop-drawer .hanlyn-qs__drawer');
  }

  function productPath(drawer) {
    var link = drawer.querySelector('.hanlyn-qs__details-link');
    if (!link) return null;
    var href = link.getAttribute('href');
    if (!href || href === '#') return null;
    return href.split('?')[0].replace(/\/+$/, '');
  }

  function fetchProduct(path) {
    if (!productCache[path]) {
      productCache[path] = fetch(path + '.js', { headers: { Accept: 'application/json' } }).then(function (r) {
        if (!r.ok) throw new Error('product json ' + r.status);
        return r.json();
      });
    }
    return productCache[path];
  }

  /**
   * Give the cloned form and its inputs their own ids so that, on a product
   * page, the drawer's radios do not join the PDP's radio group (same name +
   * same form owner) and the drawer's form is not shadowed by the PDP's.
   *
   * DO NOT read or write form.id here — use getAttribute / setAttribute.
   *
   * HTMLFormElement carries a legacy named getter that overrides built-in
   * properties, so when a form contains <input name="id"> — which every Shopify
   * product form does — `form.id` evaluates to that INPUT ELEMENT rather than
   * the id string, and `form.id = '...'` never reaches the attribute. The
   * original guard `form.id === QS_FORM_ID` was therefore never true. This
   * function re-ran on every observer callback and re-prefixed the radios each
   * time, producing names like `qs-qs-Size-1` that grew without bound.
   */
  function namespaceClone(drawer) {
    var variantRoot = drawer.querySelector('.hanlyn-qs__variants');
    var formWrapper = drawer.querySelector('.hanlyn-qs__form-wrapper');
    if (!variantRoot || !formWrapper) return;

    var form = formWrapper.querySelector('form');
    if (!form || form.getAttribute('id') === QS_FORM_ID) return;

    form.setAttribute('id', QS_FORM_ID);

    var selects = variantRoot.querySelector('variant-selects');
    if (selects) selects.removeAttribute('id');

    Array.prototype.forEach.call(variantRoot.querySelectorAll('[form]'), function (el) {
      el.setAttribute('form', QS_FORM_ID);
    });

    // Second, independent guard: never prefix the same input twice, no matter
    // what calls this or how often. A fresh clone arrives without the flag.
    Array.prototype.forEach.call(variantRoot.querySelectorAll('input[type="radio"]'), function (input) {
      if (input.dataset.hanlynQsNamespaced === 'true') return;
      var label = input.id ? variantRoot.querySelector('label[for="' + input.id + '"]') : null;
      input.id = 'qs-' + input.id;
      input.name = 'qs-' + input.name;
      if (label) label.setAttribute('for', input.id);
      input.dataset.hanlynQsNamespaced = 'true';
    });
  }

  /** Selected option values, in option order. */
  function selectedValues(variantRoot) {
    var values = [];
    Array.prototype.forEach.call(variantRoot.querySelectorAll('fieldset'), function (fieldset) {
      var checked = fieldset.querySelector('input[type="radio"]:checked');
      values.push(checked ? checked.value : null);
    });
    Array.prototype.forEach.call(variantRoot.querySelectorAll('select'), function (select) {
      values.push(select.value);
    });
    return values;
  }

  function formatMoney(cents) {
    try {
      var currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency }).format(cents / 100);
    } catch (e) {
      return '$' + (cents / 100).toFixed(2);
    }
  }

  /**
   * Push the currently selected variant into the cloned form.
   * Every write below is conditional — see BUG 1 in the header. Do not
   * "simplify" these back into unconditional assignments.
   */
  function sync() {
    var drawer = drawerRoot();
    if (!drawer || !currentProduct) return;

    var variantRoot = drawer.querySelector('.hanlyn-qs__variants');
    var formWrapper = drawer.querySelector('.hanlyn-qs__form-wrapper');
    if (!variantRoot || !formWrapper) return;

    var chosen = selectedValues(variantRoot);
    if (!chosen.length) return;
    for (var i = 0; i < chosen.length; i++) {
      if (chosen[i] === null) return;
    }

    var match = null;
    for (var v = 0; v < currentProduct.variants.length; v++) {
      var variant = currentProduct.variants[v];
      if (variant.options.length !== chosen.length) continue;
      var ok = true;
      for (var o = 0; o < chosen.length; o++) {
        if (variant.options[o] !== chosen[o]) { ok = false; break; }
      }
      if (ok) { match = variant; break; }
    }
    if (!match) return;

    // Pre-launch: see the 2026-10-21 note in the header.
    var prelaunch = !!formWrapper.querySelector('.hanlyn-launch-note');

    applyDom(function () {
      var idInput = formWrapper.querySelector('input[name="id"]');
      if (idInput) {
        if (idInput.value !== String(match.id)) idInput.value = String(match.id);
        if (idInput.hasAttribute('disabled')) idInput.removeAttribute('disabled');
        if (idInput.disabled) idInput.disabled = false;
      }

      var priceContainer = drawer.querySelector('.hanlyn-qs__price');
      if (priceContainer) {
        var priceTarget = priceContainer.querySelector('.price-item--regular') ||
          priceContainer.querySelector('.price-item') ||
          priceContainer;
        setText(priceTarget, formatMoney(match.price));
      }

      var submit = formWrapper.querySelector('[type="submit"]');
      if (submit) {
        var label = submit.querySelector('span');
        if (prelaunch) {
          if (!submit.disabled) submit.disabled = true;
          if (submit.getAttribute('aria-disabled') !== 'true') submit.setAttribute('aria-disabled', 'true');
        } else if (match.available) {
          if (submit.disabled) submit.disabled = false;
          if (submit.hasAttribute('disabled')) submit.removeAttribute('disabled');
          if (submit.hasAttribute('aria-disabled')) submit.removeAttribute('aria-disabled');
          if (label && window.variantStrings && window.variantStrings.addToCart) {
            setText(label, window.variantStrings.addToCart);
          }
        } else {
          if (!submit.hasAttribute('disabled')) submit.setAttribute('disabled', 'disabled');
          if (label && window.variantStrings && window.variantStrings.soldOut) {
            setText(label, window.variantStrings.soldOut);
          }
        }
      }
    });
  }

  /** Runs when the drawer is populated with a different product. */
  function onDrawerPopulated() {
    var drawer = drawerRoot();
    if (!drawer) return;

    // Self-guarding: only acts on a freshly cloned, not-yet-namespaced form.
    applyDom(function () {
      namespaceClone(drawer);
    });

    var path = productPath(drawer);
    if (!path) return;

    if (path === currentPath) {
      if (currentProduct) sync();
      return;
    }

    currentPath = path;
    currentProduct = null;

    fetchProduct(path)
      .then(function (data) {
        if (currentPath !== path) return;
        currentProduct = data;
        sync();
      })
      .catch(function (error) {
        console.error('Hanlyn quick shop variant fix: could not load product JSON', error);
      });
  }

  document.addEventListener('change', function (event) {
    if (!event.target.closest) return;
    if (!event.target.closest('.hanlyn-qs__variants')) return;
    sync();
  });

  // Labels are clicked rather than the radios themselves in some browsers; the
  // change event still fires, but sync again on click as a belt-and-braces pass.
  document.addEventListener('click', function (event) {
    if (!event.target.closest) return;
    if (!event.target.closest('.hanlyn-qs__variants')) return;
    setTimeout(sync, 0);
  });

  function start() {
    var host = document.querySelector('hanlyn-quick-shop-drawer');
    if (!host) return;

    observedHost = host;
    observer = new MutationObserver(function () {
      onDrawerPopulated();
    });
    observer.observe(host, OBSERVER_CONFIG);

    onDrawerPopulated();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
